import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "@/store/toastStore";
import { ConfirmDialog } from "../components/CmsShared";
import { cmsService } from "../services/cmsService";
import { useCmsStore } from "../store/cmsStore";

export type CmsPreviewDevice = "desktop" | "tablet" | "mobile";

export interface CmsPageActions {
  dirty?: boolean;
  saving?: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  editable?: boolean;
  onSave?: () => void | Promise<void>;
  onDiscard?: () => void;
  onPreview?: () => void;
  onPublish?: () => void | Promise<void>;
  onUndo?: () => void;
  onRedo?: () => void;
}

interface CmsWorkspaceContextValue {
  globalSearch: string;
  setGlobalSearch: (q: string) => void;
  previewDevice: CmsPreviewDevice;
  setPreviewDevice: (d: CmsPreviewDevice) => void;
  refreshKey: number;
  refresh: () => void;
  pageActions: CmsPageActions;
  registerPageActions: (actions: CmsPageActions) => void;
  clearPageActions: () => void;
  extraBreadcrumbs: Array<{ label: string; to?: string }>;
  setExtraBreadcrumbs: (crumbs: Array<{ label: string; to?: string }>) => void;
  navigateWithGuard: (to: string) => void;
}

const CmsWorkspaceContext = createContext<CmsWorkspaceContextValue | null>(null);

function isInternalRoute(href: string): boolean {
  if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) {
    return false;
  }
  if (href.startsWith("http://") || href.startsWith("https://")) {
    try {
      return new URL(href).origin === window.location.origin;
    } catch {
      return false;
    }
  }
  return href.startsWith("/");
}

function normalizePath(href: string): string {
  if (href.startsWith("http://") || href.startsWith("https://")) {
    const url = new URL(href);
    return url.pathname + url.search + url.hash;
  }
  return href;
}

export function CmsWorkspaceProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [globalSearch, setGlobalSearch] = useState("");
  const [previewDevice, setPreviewDevice] = useState<CmsPreviewDevice>("desktop");
  const [refreshKey, setRefreshKey] = useState(0);
  const [pageActions, setPageActions] = useState<CmsPageActions>({});
  const [extraBreadcrumbs, setExtraBreadcrumbs] = useState<Array<{ label: string; to?: string }>>([]);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  const dirtyRef = useRef(false);
  const allowNavigationRef = useRef(false);
  dirtyRef.current = pageActions.dirty === true;

  const registerPageActions = useCallback((actions: CmsPageActions) => {
    setPageActions(actions);
  }, []);

  const clearPageActions = useCallback(() => {
    setPageActions({});
  }, []);

  const refresh = useCallback(() => {
    useCmsStore.getState().invalidateAll();
    setRefreshKey((k) => k + 1);
    toast("success", "Content refreshed");
  }, []);

  const currentPath = location.pathname + location.search + location.hash;

  const navigateWithGuard = useCallback(
    (to: string) => {
      if (allowNavigationRef.current || !dirtyRef.current || normalizePath(to) === currentPath) {
        navigate(to);
        return;
      }
      setPendingNavigation(to);
    },
    [navigate, currentPath]
  );

  const confirmLeave = useCallback(() => {
    if (!pendingNavigation) return;
    allowNavigationRef.current = true;
    const target = pendingNavigation;
    setPendingNavigation(null);
    navigate(target);
    requestAnimationFrame(() => {
      allowNavigationRef.current = false;
    });
  }, [navigate, pendingNavigation]);

  const cancelLeave = useCallback(() => {
    setPendingNavigation(null);
  }, []);

  useEffect(() => {
    setPendingNavigation(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!pageActions.dirty) return;

    const handleClick = (event: MouseEvent) => {
      if (allowNavigationRef.current || !dirtyRef.current) return;
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target as HTMLElement;
      if (target.closest(".sidebar, .app-shell__navbar")) return;

      const anchor = target.closest("a[href]");
      if (!anchor) return;
      if (anchor.getAttribute("target") === "_blank") return;

      const href = anchor.getAttribute("href");
      if (!href || !isInternalRoute(href)) return;

      const path = normalizePath(href);
      if (path === currentPath) return;

      event.preventDefault();
      event.stopPropagation();
      setPendingNavigation(path);
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [pageActions.dirty, currentPath]);

  useEffect(() => {
    if (!pageActions.dirty) return;

    const handlePopState = () => {
      if (allowNavigationRef.current) return;

      const targetPath = window.location.pathname + window.location.search + window.location.hash;
      if (targetPath === currentPath) return;

      window.history.pushState(null, "", currentPath);
      setPendingNavigation(targetPath);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [pageActions.dirty, currentPath]);

  useEffect(() => {
    if (!pageActions.dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [pageActions.dirty]);

  const value = useMemo(
    () => ({
      globalSearch,
      setGlobalSearch,
      previewDevice,
      setPreviewDevice,
      refreshKey,
      refresh,
      pageActions,
      registerPageActions,
      clearPageActions,
      extraBreadcrumbs,
      setExtraBreadcrumbs,
      navigateWithGuard,
    }),
    [
      globalSearch,
      previewDevice,
      refreshKey,
      refresh,
      pageActions,
      registerPageActions,
      clearPageActions,
      extraBreadcrumbs,
      navigateWithGuard,
    ]
  );

  return (
    <CmsWorkspaceContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={pendingNavigation !== null}
        title="Unsaved Changes"
        message="You have unsaved changes. Leave without saving?"
        confirmLabel="Leave"
        onConfirm={confirmLeave}
        onClose={cancelLeave}
      />
    </CmsWorkspaceContext.Provider>
  );
}

export function useCmsWorkspace() {
  const ctx = useContext(CmsWorkspaceContext);
  if (!ctx) throw new Error("useCmsWorkspace must be used within CmsWorkspaceProvider");
  return ctx;
}

export function useCmsPageRegistration(actions: CmsPageActions) {
  const { registerPageActions, clearPageActions } = useCmsWorkspace();
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  const { dirty, saving, canUndo, canRedo, editable } = actions;

  useEffect(() => {
    registerPageActions({
      get dirty() {
        return actionsRef.current.dirty;
      },
      get saving() {
        return actionsRef.current.saving;
      },
      get canUndo() {
        return actionsRef.current.canUndo;
      },
      get canRedo() {
        return actionsRef.current.canRedo;
      },
      get editable() {
        return actionsRef.current.editable;
      },
      onSave: () => void actionsRef.current.onSave?.(),
      onDiscard: () => actionsRef.current.onDiscard?.(),
      onPreview: () => actionsRef.current.onPreview?.(),
      onPublish: () => void actionsRef.current.onPublish?.(),
      onUndo: () => actionsRef.current.onUndo?.(),
      onRedo: () => actionsRef.current.onRedo?.(),
    });
    return () => clearPageActions();
  }, [dirty, saving, canUndo, canRedo, editable, registerPageActions, clearPageActions]);
}

/** Re-run callback when workspace refresh is triggered. */
export function useCmsRefresh(onRefresh: () => void) {
  const { refreshKey } = useCmsWorkspace();
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (refreshKey > 0) onRefreshRef.current();
  }, [refreshKey]);
}

export async function cmsSaveWithToast(saveFn: () => void | Promise<void>) {
  try {
    await saveFn();
    toast("success", "Changes saved successfully");
  } catch {
    toast("error", "Failed to save changes");
  }
}

export async function cmsPublishWithToast(saveFn: () => void | Promise<void>) {
  try {
    await saveFn();
    await cmsService.publish();
    toast("success", "Published to live website");
  } catch {
    toast("error", "Failed to publish changes");
  }
}
